import { useState, type DragEvent } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Folder, GripVertical, Image as ImageIcon, Pin, X } from 'lucide-react';
import type { MemberPin } from '@/lib/api/types';
import { usePins, useReorderPins, useUnpin } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { cx } from '@/lib/format';
import { pinHref, pinLabel, reorderPins } from './pinsModel';

const DRAG_MIME = 'application/x-lumen-pin';

/** Sidebar "Pinned" section (S1): boards and assets, drag to reorder, hover × to unpin. */
export function PinnedSection({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { ws = '' } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const isMember = useSessionStore((s) => s.principal.kind === 'member');
  const pins = usePins(isMember ? wsId : undefined);
  const reorder = useReorderPins(wsId);
  const unpin = useUnpin(wsId);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const items = pins.data ?? [];
  if (!isMember || items.length === 0) return null;

  const onDragStart = (e: DragEvent, p: MemberPin) => {
    e.dataTransfer.setData(DRAG_MIME, p.id);
    e.dataTransfer.effectAllowed = 'move';
    setDragId(p.id);
  };
  const onDragOver = (e: DragEvent, p: MemberPin) => {
    if (!dragId || !e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== p.id) setOverId(p.id);
  };
  const onDrop = (e: DragEvent, p: MemberPin) => {
    e.preventDefault();
    const id = e.dataTransfer.getData(DRAG_MIME) || dragId;
    setDragId(null);
    setOverId(null);
    if (!id || id === p.id) return;
    const next = reorderPins(items, id, p.id);
    if (next !== items) reorder.mutate(next.map((x) => x.id));
  };
  const onKeyMove = (p: MemberPin, dir: -1 | 1) => {
    const i = items.findIndex((x) => x.id === p.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= items.length) return;
    reorder.mutate(reorderPins(items, p.id, items[j]!.id).map((x) => x.id));
  };

  return (
    <section className="mt-2 px-2" aria-label="Pinned" data-testid="pinned-section">
      <div className={cx('flex items-center gap-1 px-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase', collapsed && 'justify-center')}>
        <Pin size={13} />
        {!collapsed && <span>Pinned</span>}
      </div>
      <ul className="mt-1 space-y-px" role="list">
        {items.map((p) => {
          const archived = !!p.archived_at;
          return (
            <li
              key={p.id}
              draggable={!collapsed}
              onDragStart={(e) => onDragStart(e, p)}
              onDragOver={(e) => onDragOver(e, p)}
              onDragEnter={(e) => onDragOver(e, p)}
              onDrop={(e) => onDrop(e, p)}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              className={cx('group flex items-center gap-1 rounded-md pr-1 text-sm', overId === p.id && dragId !== p.id && 'ring-1 ring-accent', dragId === p.id && 'opacity-50')}
              data-testid="pinned-item"
              data-kind={p.kind}
            >
              {!collapsed && <GripVertical size={12} className="invisible shrink-0 cursor-grab text-fg-subtle group-hover:visible" aria-hidden />}
              <NavLink
                to={pinHref(ws, p)}
                onClick={onNavigate}
                title={pinLabel(p) + (archived ? ' (archived)' : '')}
                className={({ isActive }) => cx('flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-1 hover:bg-bg-sunken', isActive && 'bg-bg-sunken font-medium text-accent', archived && 'opacity-60', collapsed && 'justify-center')}
                onKeyDown={(e) => {
                  if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                    e.preventDefault();
                    onKeyMove(p, e.key === 'ArrowUp' ? -1 : 1);
                  }
                }}
              >
                {p.kind === 'board' ? <Folder size={14} className="shrink-0" /> : <ImageIcon size={14} className="shrink-0" />}
                {!collapsed && <span className={cx('truncate', archived && 'line-through decoration-fg-subtle')}>{pinLabel(p)}</span>}
                {!collapsed && archived && <span className="ml-auto shrink-0 text-[10px] text-fg-subtle uppercase">archived</span>}
              </NavLink>
              {!collapsed && (
                <button type="button" aria-label={`Unpin ${pinLabel(p)}`} className="invisible h-5 w-5 shrink-0 rounded text-fg-subtle group-hover:visible hover:bg-bg-sunken" onClick={() => unpin.mutate({ kind: p.kind, targetId: p.target_id })}>
                  <X size={12} className="mx-auto" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
